// src/pages/app_event_banner.js
import React, { useEffect, useMemo, useState } from "react";
import PermissionGuard from "../components/PermissionGuard";
import {
  getDownloadURL,
  listAll,
  ref as storageRef,
  uploadBytesResumable,
  deleteObject,
  getMetadata,
} from "firebase/storage";
import { storage } from "../firebase"; // ensure your firebase exports `storage`
import { FiUpload, FiImage, FiTrash2, FiRefreshCw, FiX, FiEye, FiSettings } from "react-icons/fi";

/*
 * Admin page to manage Event App banners in Firebase Storage.
 * Requirements from user:
 * - Two tabs: 1) Upload Banner, 2) View/Delete Banner
 * - Folder: `event_app_banner/`
 * - Top banner stored as `top.webp`
 * - Up to 10 banners: `banner1.webp` ... `banner10.webp`
 * - Always compress to WebP before upload (client-side here). 
 * - Show success message after upload; allow delete and replace from Manage tab.
 */

const COLORS = {
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  primary: "#f20c53",
  secondary: "#fb6c33",
  ok: "#16a34a",
  warn: "#b91c1c",
  page: "#f5f6f8",
};

const SLOTS = ["top", ...Array.from({ length: 10 }, (_, i) => `banner${i + 1}`)];

export default function EventAppBanners() {
  const [tab, setTab] = useState("upload"); // upload | manage
  const [items, setItems] = useState([]); // list of { name, url?, exists, size?, updatedAt? }
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState("");

  // Upload modal
  const [modalOpen, setModalOpen] = useState(false);
  const [chosenSlot, setChosenSlot] = useState("top");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    refreshList();
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 1600);
    return () => clearTimeout(t);
  }, [toast]);

  async function refreshList() {
    setLoading(true);
    setErr("");
    try {
      const folderRef = storageRef(storage, "event_app_banner");
      const res = await listAll(folderRef);
      // Build lookup for found items
      const found = {};
      await Promise.all(
        res.items.map(async (it) => {
          try {
            const meta = await getMetadata(it);
            const url = await getDownloadURL(it);
            const base = (it.name || "").replace(/\.[^.]+$/, "");
            found[base] = {
              name: base,
              url,
              exists: true,
              size: meta.size || 0,
              updatedAt: meta.updated || null,
              contentType: meta.contentType || "",
            };
          } catch (e) {
            // ignore individual errors
          }
        })
      );
      const list = SLOTS.map((name) => ({
        name,
        path: `event_app_banner/${name}.webp`,
        ...(found[name] || { exists: false }),
      }));
      setItems(list);
    } catch (e) {
      console.error(e);
      setErr("Failed to load banners.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(slot) {
    if (!window.confirm(`Delete ${slot}? This cannot be undone.`)) return;
    try {
      const ref = storageRef(storage, `event_app_banner/${slot}.webp`);
      await deleteObject(ref);
      showToast("Banner deleted");
      await refreshList();
    } catch (e) {
      console.error(e);
      showToast("Delete failed");
    }
  }

  function openUpload(slot) {
    setChosenSlot(slot || "top");
    setFile(null);
    setProgress(0);
    setModalOpen(true);
  }

  async function handleUpload(e) {
    e?.preventDefault?.();
    if (!file) return;
    try {
      setUploading(true);
      setProgress(1);
      const blob = await compressToWebP(file, { maxW: 1920, maxH: 1080, quality: 0.82 });
      const ref = storageRef(storage, `event_app_banner/${chosenSlot}.webp`);
      const metadata = { contentType: "image/webp", cacheControl: "public,max-age=31536000,immutable" };
      const task = uploadBytesResumable(ref, blob, metadata);
      await new Promise((resolve, reject) => {
        task.on(
          "state_changed",
          (snap) => {
            if (snap.totalBytes) {
              setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
            }
          },
          (err) => reject(err),
          () => resolve()
        );
      });
      setModalOpen(false);
      showToast("Banner uploaded");
      await refreshList();
    } catch (e) {
      console.error(e);
      showToast("Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
      setFile(null);
    }
  }

  function showToast(msg) {
    setToast(msg);
  }

  const counts = useMemo(() => {
    const existing = items.filter((x) => x.exists).length;
    return { existing, totalSlots: SLOTS.length };
  }, [items]);

  return (
    <PermissionGuard requiredPermission="manage_event_app_banners">
      <div style={{ padding: 16, background: COLORS.page, minHeight: "100vh", fontFamily: "Inter, system-ui, sans-serif" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: 0, fontWeight: 900, color: COLORS.text }}>Event App Banners</h2>
            <p style={{ margin: "4px 0 0", color: COLORS.subtext }}>
              Uploads are compressed to WebP and stored under <code>event_app_banner/</code>.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8, minWidth: 240 }}>
            <StatCard label="Slots Used" value={`${counts.existing}/${counts.totalSlots}`} tone="accepted" />
            <StatCard label="Folder" value="event_app_banner" />
          </div>
        </div>

        {/* Tabs */}
        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={() => setTab("upload")}
            style={{ ...tabBtn, ...(tab === "upload" ? tabBtnActive : null) }}
            aria-pressed={tab === "upload"}
          >
            <FiUpload /> Upload Banner
          </button>
          <button
            onClick={() => setTab("manage")}
            style={{ ...tabBtn, ...(tab === "manage" ? tabBtnActive : null) }}
            aria-pressed={tab === "manage"}
          >
            <FiSettings /> View / Delete / Replace
          </button>
          <button onClick={refreshList} style={{ ...iconBtn, width: 40 }} title="Refresh list" aria-label="Refresh list">
            <FiRefreshCw />
          </button>
        </div>

        {/* Content */}
        {tab === "upload" ? (
          <div style={card}>
            <h3 style={h3}>Upload banners</h3>
            <p style={{ color: COLORS.subtext, marginTop: -6 }}>Pick a slot (Top or Banner 1–10), choose an image, we'll compress to WebP, and upload.</p>
            <div style={{ display: "grid", gap: 12 }}>
              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ fontWeight: 800, color: COLORS.text }}>Choose slot</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 8 }}>
                  {SLOTS.map((s) => (
                    <label key={s} style={slotPill(s === chosenSlot)}>
                      <input
                        type="radio"
                        name="slot"
                        value={s}
                        checked={chosenSlot === s}
                        onChange={(e) => setChosenSlot(e.target.value)}
                        style={{ display: "none" }}
                      />
                      {s === "top" ? "Top" : s.replace("banner", "Banner ")}
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ fontWeight: 800, color: COLORS.text }}>Select image</div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  style={fileInput}
                />
                <div style={{ fontSize: 12, color: COLORS.subtext }}>
                  Tip: Use high-quality JPG/PNG; we'll resize to max 1920×1080 and convert to WebP.
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  style={primaryBtn}
                  disabled={!file || uploading}
                  onClick={handleUpload}
                  title="Upload banner"
                >
                  <FiUpload /> {uploading ? `Uploading… ${progress}%` : "Upload"}
                </button>
                {file && !uploading && (
                  <div style={{ fontSize: 12, color: COLORS.subtext }}>{file.name}</div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={card}>
            <h3 style={h3}>Manage banners</h3>
            {loading && <div style={{ color: COLORS.subtext, marginTop: 10 }}>Loading…</div>}
            {err && <div style={{ color: COLORS.warn, marginTop: 10 }}>{err}</div>}
            {!loading && !err && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr>
                      <Th sticky>Slot</Th>
                      <Th sticky>Preview</Th>
                      <Th sticky>Size</Th>
                      <Th sticky>Updated</Th>
                      <Th sticky>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => {
                      const rowBg = idx % 2 ? "#fafafa" : "#fff";
                      return (
                        <tr
                          key={it.name}
                          style={{ borderTop: `1px solid ${COLORS.border}`, background: rowBg }}
                        >
                          <Td style={{ fontWeight: 800 }}>
                            {it.name === "top" ? "Top" : it.name.replace("banner", "Banner ")}
                            {!it.exists && <span style={{ marginLeft: 8, color: COLORS.subtext }}>(empty)</span>}
                          </Td>
                          <Td>
                            {it.exists ? (
                              <a href={it.url} target="_blank" rel="noreferrer" title="Open image">
                                <img src={it.url} alt={it.name} style={{ width: 160, height: 80, objectFit: "cover", borderRadius: 8, border: `1px solid ${COLORS.border}` }} />
                              </a>
                            ) : (
                              <div style={{ display: "inline-grid", placeItems: "center", width: 160, height: 80, border: `1px dashed ${COLORS.border}`, borderRadius: 8, color: COLORS.subtext }}>
                                <FiImage />
                              </div>
                            )}
                          </Td>
                          <Td>{it.exists ? humanSize(it.size) : "—"}</Td>
                          <Td>{it.exists ? formatDateTime(it.updatedAt) : "—"}</Td>
                          <Td>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <button style={iconBtn} title="Replace" onClick={() => openUpload(it.name)}>
                                <FiRefreshCw />
                              </button>
                              <button
                                style={{ ...iconBtn, color: it.exists ? COLORS.warn : COLORS.subtext }}
                                title={it.exists ? "Delete" : "Nothing to delete"}
                                onClick={() => it.exists && handleDelete(it.name)}
                                disabled={!it.exists}
                              >
                                <FiTrash2 />
                              </button>
                              {it.exists && (
                                <a href={it.url} target="_blank" rel="noreferrer" style={{ ...iconBtn, display: "grid", placeItems: "center", textDecoration: "none" }} title="Open">
                                  <FiEye />
                                </a>
                              )}
                            </div>
                          </Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Modal for upload/replace */}
        {modalOpen && (
          <div style={modalBackdrop} onClick={(e) => e.target === e.currentTarget && !uploading && setModalOpen(false)}>
            <div style={modalCard} role="dialog" aria-modal="true" aria-label="Upload banner">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontWeight: 900, color: COLORS.text }}>Upload {chosenSlot === "top" ? "Top" : chosenSlot.replace("banner", "Banner ")}</div>
                <button onClick={() => setModalOpen(false)} style={iconBtn} aria-label="Close" disabled={uploading}>
                  <FiX />
                </button>
              </div>
              <div style={{ display: "grid", gap: 12 }}>
                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ fontWeight: 800, color: COLORS.text }}>Select image</div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    style={fileInput}
                    disabled={uploading}
                  />
                  <div style={{ fontSize: 12, color: COLORS.subtext }}>
                    Will be compressed to WebP (max 1920×1080) before upload.
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button style={primaryBtn} disabled={!file || uploading} onClick={handleUpload}>
                    <FiUpload /> {uploading ? `Uploading… ${progress}%` : "Upload"}
                  </button>
                  {file && !uploading && <div style={{ fontSize: 12, color: COLORS.subtext }}>{file.name}</div>}
                </div>
              </div>
            </div>
          </div>
        )}

        {toast && <div style={toastBox}>{toast}</div>}
      </div>
    </PermissionGuard>
  );
}

/* Helpers */
function humanSize(n = 0) {
  try {
    if (n < 1024) return `${n} B`;
    if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
    return `${(n / 1024 ** 3).toFixed(1)} GB`;
  } catch {
    return `${n}`;
  }
}

function formatDateTime(v) {
  try {
    if (!v) return "";
    const d = typeof v === "string" ? new Date(v) : new Date(v);
    return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(d);
  } catch {
    return "";
  }
}

async function compressToWebP(file, { maxW = 1920, maxH = 1080, quality = 0.82 } = {}) {
  const src = URL.createObjectURL(file);
  try {
    const img = await loadImage(src);
    const scale = Math.min(maxW / img.width, maxH / img.height, 1);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("WebP encode failed"))), "image/webp", quality)
    );
    return blob;
  } finally {
    URL.revokeObjectURL(src);
  }
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.crossOrigin = "anonymous";
    img.src = url;
  });
}

/* Parts */
function Th({ children, sticky }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: 10,
        fontWeight: 800,
        color: COLORS.text,
        background: "#f8fafc",
        position: sticky ? "sticky" : "static",
        top: 0,
        zIndex: 5,
        borderBottom: `1px solid ${COLORS.border}`,
      }}
    >
      {children}
    </th>
  );
}
function Td({ children, style }) {
  return <td style={{ padding: 10, color: COLORS.text, verticalAlign: "top", ...style }}>{children}</td>;
}

function StatCard({ label, value, tone }) {
  const palette =
    tone === "pending"
      ? { bg: "#fff7ed", border: "#fed7aa", text: "#92400e" }
      : tone === "accepted"
      ? { bg: "#eff6ff", border: "#bfdbfe", text: "#1e3a8a" }
      : tone === "completed"
      ? { bg: "#ecfdf5", border: "#a7f3d0", text: "#065f46" }
      : { bg: "#fff", border: COLORS.border, text: COLORS.text };
  return (
    <div
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 10,
        padding: "10px 12px",
        minWidth: 120,
        display: "grid",
        gap: 2,
      }}
    >
      <div style={{ fontSize: 12, color: COLORS.subtext }}>{label}</div>
      <div style={{ fontWeight: 900, color: palette.text, fontSize: 18 }}>{value}</div>
    </div>
  );
}

/* Styles */
const card = {
  marginTop: 12,
  background: COLORS.card,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 12,
  boxShadow: "0 6px 18px rgba(0,0,0,.06)",
  padding: 14,
};
const h3 = { margin: 0, color: COLORS.text };

const tabBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  height: 38,
  padding: "0 12px",
  borderRadius: 10,
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  fontWeight: 800,
};
const tabBtnActive = {
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  color: "#fff",
  border: "none",
};

const primaryBtn = {
  height: 38,
  padding: "0 12px",
  borderRadius: 10,
  border: "none",
  color: "#fff",
  fontWeight: 800,
  cursor: "pointer",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
};

const iconBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  width: 34,
  height: 34,
  borderRadius: 8,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};

const fileInput = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  borderRadius: 10,
  padding: 10,
};

const slotPill = (active) => ({
  border: `1px solid ${active ? COLORS.secondary : COLORS.border}`,
  background: active ? "#fff7f9" : "#fff",
  color: COLORS.text,
  borderRadius: 999,
  padding: "8px 12px",
  textAlign: "center",
  cursor: "pointer",
  fontWeight: 800,
});

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.45)",
  display: "grid",
  placeItems: "center",
  zIndex: 60,
  padding: 16,
};

const modalCard = {
  width: "100%",
  maxWidth: 560,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 14,
  padding: 14,
  boxShadow: "0 10px 30px rgba(0,0,0,.25)",
};

const toastBox = {
  position: "fixed",
  bottom: 16,
  right: 16,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  boxShadow: "0 8px 24px rgba(0,0,0,.12)",
  color: COLORS.text,
  fontWeight: 800,
  zIndex: 80,
};
